/**
 * Generic CSV exporter.
 *
 * Escaping rules (per RFC 4180-ish, Excel-compatible):
 *   - Wrap values in "..." when they contain comma, quote, CR, LF, or
 *     leading/trailing whitespace.
 *   - Escape double-quotes by doubling them.
 *   - Use CRLF line endings.
 *   - Prepend a UTF-8 BOM so Excel opens accented characters correctly.
 *
 * No PHI should be exported via this util. The current callers (batches,
 * audit log, team) only export operational data.
 */

export interface CsvColumn<T> {
  header: string;
  accessor: (row: T) => string | number | boolean | null | undefined;
}

export interface ExportCsvOptions<T> {
  filename: string;
  columns: ReadonlyArray<CsvColumn<T>>;
  rows: ReadonlyArray<T>;
}

export function exportCsv<T>({ filename, columns, rows }: ExportCsvOptions<T>): void {
  const lines: string[] = [];
  // Header row
  lines.push(columns.map((c) => escape(c.header)).join(','));
  // Data rows
  for (const row of rows) {
    lines.push(columns.map((c) => escape(c.accessor(row))).join(','));
  }
  // CRLF + BOM
  const csv = '﻿' + lines.join('\r\n') + '\r\n';

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Free the blob ref on next tick.
  window.setTimeout(() => URL.revokeObjectURL(url), 100);
}

/** Convert any value into a CSV cell string with proper escaping. */
function escape(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return '';
  const s = typeof value === 'string' ? value : String(value);
  // Quote when needed.
  const needsQuoting =
    s.includes(',') ||
    s.includes('"') ||
    s.includes('\r') ||
    s.includes('\n') ||
    /^\s|\s$/.test(s);
  if (!needsQuoting) return s;
  return `"${s.replace(/"/g, '""')}"`;
}

/** Build a filename with date stamp. e.g., "batches-2026-05-06.csv" */
export function csvFilename(prefix: string): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${prefix}-${y}-${m}-${day}.csv`;
}
